<template>
  <modal
    :name="modalName"
    :width="560"
    :height="'auto'"
    :adaptive="true"
    :scrollable="true"
    :click-to-close="false"
    class="employee-leave-request-modal"
    @opened="onOpened"
  >
    <div
      class="employee-leave-request-modal__shell"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="controlIds.title"
    >
      <AppModalHeader
        ref="header"
        design="new"
        eyebrow="Employee leave"
        title="Create leave request"
        :title-id="controlIds.title"
        @close="close"
      />

      <form class="employee-leave-request-modal__form" @submit.prevent="submit">
        <div class="employee-leave-request-modal__body">
          <div class="employee-leave-request-modal__employee">
            <span>Employee</span>
            <strong>{{ employeeName }}</strong>
          </div>

          <div v-if="typesError" class="employee-leave-request-modal__alert" role="alert">
            <span>{{ typesError }}</span>
            <AppButton design="new" theme="dark" size="small" @click.native="fetchLeaveTypes">
              Retry
            </AppButton>
          </div>

          <AppFormGroup
            design="new"
            label="Leave type"
            :label-for="controlIds.type"
            required
          >
            <AppSelect
              v-model="form.type"
              design="new"
              :input-id="controlIds.type"
              :options="leaveTypeOptions"
              :clearable="false"
              :loading="typesLoading"
              :disabled="typesLoading || Boolean(typesError)"
              placeholder="Leave type"
              :error="$v.form.type.$error"
              @input="onLeaveTypeChange"
            />
            <template #error>
              <div v-if="$v.form.type.$dirty && !$v.form.type.required">Required field</div>
            </template>
          </AppFormGroup>

          <AppFormGroup
            v-if="parentalDateRequired"
            design="new"
            :label="parentalDateLabel"
            :label-for="controlIds.parentalDate"
            required
          >
            <AppDatePicker
              v-model="form.parental_date"
              design="new"
              :input-attr="{ id: controlIds.parentalDate }"
              :placeholder="parentalDateLabel"
              :disabled-date="isParentalDateDisabled"
              :error="$v.form.parental_date.$error"
              @input="onParentalDateChange"
            />
            <template #error>
              <div v-if="$v.form.parental_date.$dirty && !$v.form.parental_date.parentalDatePresent">
                Required field
              </div>
              <div v-if="$v.form.parental_date.$dirty && !$v.form.parental_date.parentalDateInAllowedRange">
                Birth Date cannot be later than 6 months from today
              </div>
            </template>
          </AppFormGroup>

          <div class="employee-leave-request-modal__dates">
            <AppFormGroup
              design="new"
              label="From date"
              :label-for="controlIds.fromDate"
              required
            >
              <AppDatePicker
                v-model="form.from_date"
                design="new"
                :input-attr="{ id: controlIds.fromDate }"
                placeholder="From date"
                :readonly="parentalDateRequired && !form.parental_date"
                :disabled-date="isFromDateDisabled"
                :error="$v.form.from_date.$error"
              />
              <template #error>
                <div v-if="$v.form.from_date.$dirty && !$v.form.from_date.required">Required field</div>
                <div v-if="$v.form.from_date.$dirty && !$v.form.from_date.fromDateInAllowedRange">
                  {{ fromDateRangeError }}
                </div>
              </template>
            </AppFormGroup>

            <AppFormGroup
              design="new"
              label="To date"
              :label-for="controlIds.toDate"
              required
            >
              <AppDatePicker
                v-model="form.to_date"
                design="new"
                :input-attr="{ id: controlIds.toDate }"
                placeholder="To date"
                :readonly="parentalDateRequired && !form.parental_date"
                :disabled-date="isToDateDisabled"
                :error="$v.form.to_date.$error"
              />
              <template #error>
                <div v-if="$v.form.to_date.$dirty && !$v.form.to_date.required">Required field</div>
                <div v-if="$v.form.to_date.$dirty && !$v.form.to_date.afterFromDate">
                  To date cannot be earlier than from date
                </div>
                <div v-if="$v.form.to_date.$dirty && !$v.form.to_date.maximumLeaveDuration">
                  Leave cannot exceed {{ maximumLeaveDays }} days
                </div>
              </template>
            </AppFormGroup>
          </div>

          <AppFormGroup
            design="new"
            label="Reason"
            :label-for="controlIds.reason"
            required
          >
            <AppTextarea
              v-model="form.reason"
              :id="controlIds.reason"
              design="new"
              placeholder="Reason"
              :error="$v.form.reason.$error"
            />
            <template #error>
              <div v-if="$v.form.reason.$dirty && !$v.form.reason.required">Required field</div>
            </template>
          </AppFormGroup>

          <AppFormGroup
            v-if="proofShownOnCreate"
            design="new"
            label="Supporting document"
            :label-for="controlIds.proof"
            :required="proofRequiredOnSubmit"
          >
            <AppUploader
              v-model="form.proof"
              design="new"
              :input-id="controlIds.proof"
              :extensions="proofExtensions"
              class="custom-uploader--flex"
              form-data-formats="DOC,DOCX,XLS,XLSX,ZIP,PDF,JPEG,BMP,PNG,TIFF"
            />
            <template #error>
              <div v-if="$v.form.proof.$dirty && !$v.form.proof.proofPresent">Required field</div>
            </template>
          </AppFormGroup>

          <div v-if="submitError" class="employee-leave-request-modal__alert" role="alert">
            {{ submitError }}
          </div>
        </div>

        <div class="employee-leave-request-modal__footer">
          <AppButton design="new" theme="dark" :disabled="submitting" @click.native="close">
            Cancel
          </AppButton>
          <AppButton design="new" type="submit" :disabled="submitting || typesLoading || Boolean(typesError)">
            {{ submitting ? 'Creating…' : 'Create request' }}
          </AppButton>
          <AppButton design="new" type="button" :disabled="submitting || typesLoading || Boolean(typesError)" @click.native="submit(true)">Create &amp; approve</AppButton>
        </div>
      </form>
    </div>
  </modal>
</template>

<script>
import { addDays, addMonths, addYears, differenceInCalendarDays, format, parseISO, subDays } from 'date-fns'
import { required } from 'vuelidate/lib/validators'
import { geEmployeesLeaveTypes, postEmployeesLeaveCreateAndApprove, postEmployeesLeaveRequest } from '@/http'

let modalInstanceCounter = 0

const createModalInstance = () => {
  modalInstanceCounter += 1
  const prefix = `employee-leave-request-${modalInstanceCounter}`
  return {
    modalName: prefix,
    controlIds: {
      title: `${prefix}-title`,
      type: `${prefix}-type`,
      parentalDate: `${prefix}-parental-date`,
      fromDate: `${prefix}-from-date`,
      toDate: `${prefix}-to-date`,
      reason: `${prefix}-reason`,
      proof: `${prefix}-proof`,
    },
  }
}

const LEAVE_DAYS_LIMITS = {
  paid_leave: 28,
  unpaid_leave: 28,
  sick_leave: 28,
  maternity_leave: 90,
  paternity_leave: 15
}

const createEmptyForm = () => ({
  type: null,
  parental_date: null,
  from_date: null,
  to_date: null,
  reason: '',
  proof: null
})
const dateValue = date => format(date, 'yyyy-MM-dd')
const isParentalDateRequired = type => Boolean(type && type.parental_date_required)
const parentalDatePresent = (value, form) => !isParentalDateRequired(form.type) || Boolean(value)
const parentalDateInAllowedRange = (value, form) => {
  if (!value || !form.type || form.type.code !== 'paternity_leave') return true
  return value <= dateValue(addMonths(new Date(), 6))
}
const fromDateInAllowedRange = (value, form) => {
  if (!value || !form.type) return true
  const today = dateValue(new Date())
  if (['paid_leave', 'unpaid_leave'].includes(form.type.code)) {
    return value >= today && value <= dateValue(addYears(new Date(), 1))
  }
  if (form.type.code === 'sick_leave') {
    return value >= dateValue(subDays(new Date(), 1)) && value <= today
  }
  if (form.type.code === 'paternity_leave' && form.parental_date) {
    return value >= dateValue(subDays(parseISO(form.parental_date), 3))
      && value <= dateValue(addMonths(parseISO(form.parental_date), 6))
  }
  return true
}
const afterFromDate = (value, form) => !value || !form.from_date || value >= form.from_date
function maximumLeaveDuration(value, form) {
  if (!value || !form.from_date || !form.type) return true
  const maximumDays = this.maximumLeaveDays
  if (!maximumDays || value < form.from_date) return true
  return differenceInCalendarDays(parseISO(value), parseISO(form.from_date)) < maximumDays
}
const getProofRequirement = type => type && type.proof_requirement
const isProofRequiredOnSubmit = type => {
  const requirement = getProofRequirement(type)
  return Boolean(requirement && requirement.required_on_submit === true)
}
const isProofAcceptedOnCreate = type => {
  const requirement = getProofRequirement(type)
  return Boolean(requirement && requirement.accepted_on_create === true)
}
const proofPresent = (value, form) => !isProofRequiredOnSubmit(form.type) || Boolean(value && value.id)

export default {
  name: 'EmployeeLeaveRequestModal',
  props: {
    employee: {
      type: Object,
      required: true
    }
  },
  data() {
    const modalInstance = createModalInstance()
    return {
      modalName: modalInstance.modalName,
      controlIds: modalInstance.controlIds,
      form: createEmptyForm(),
      leaveTypeOptions: [],
      typesLoading: false,
      typesError: '',
      submitting: false,
      submitError: '',
      proofExtensions: [
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/zip',
        'application/pdf',
        'image/jpeg',
        'image/png',
        'image/tiff',
        'image/bmp'
      ]
    }
  },
  validations: {
    form: {
      type: { required },
      parental_date: { parentalDatePresent, parentalDateInAllowedRange },
      from_date: { required, fromDateInAllowedRange },
      to_date: { required, afterFromDate, maximumLeaveDuration },
      reason: { required },
      proof: { proofPresent }
    }
  },
  computed: {
    employeeName() {
      return this.employee.full_name
        || [this.employee.first_name, this.employee.last_name].filter(Boolean).join(' ')
        || `Employee #${this.employee.id}`
    },
    proofRequiredOnSubmit() {
      return isProofRequiredOnSubmit(this.form.type)
    },
    proofShownOnCreate() {
      return this.proofRequiredOnSubmit || isProofAcceptedOnCreate(this.form.type)
    },
    parentalDateRequired() {
      return isParentalDateRequired(this.form.type)
    },
    parentalDateLabel() {
      return this.form.type && this.form.type.code === 'maternity_leave' ? 'Expected Birth Date' : 'Birth Date'
    },
    maximumLeaveDays() {
      // Temporary Safari guide policy, matching resident leave validation.
      if (this.form.type && this.form.type.code === 'paid_leave'
        && this.employee.position && this.employee.position.code === 'safari_driver') return null
      return this.form.type ? LEAVE_DAYS_LIMITS[this.form.type.code] : null
    },
    fromDateRangeError() {
      if (!this.form.type) return ''
      if (['paid_leave', 'unpaid_leave'].includes(this.form.type.code)) {
        return 'From date must be between today and one year from today'
      }
      if (this.form.type.code === 'sick_leave') return 'From date must be yesterday or today'
      if (this.form.type.code === 'paternity_leave') {
        return 'From date must be between 3 days before Birth Date and 6 months after Birth Date'
      }
      return ''
    }
  },
  methods: {
    open() {
      this.form = createEmptyForm()
      this.submitError = ''
      this.$v.form.$reset()
      if (!this.leaveTypeOptions.length) this.fetchLeaveTypes()
      this.$modal.show(this.modalName)
    },
    close() {
      if (this.submitting) return
      this.$modal.hide(this.modalName)
    },
    onOpened() {
      this.$nextTick(() => {
        if (this.$refs.header) this.$refs.header.focusClose()
      })
    },
    fetchLeaveTypes() {
      this.typesLoading = true
      this.typesError = ''
      return geEmployeesLeaveTypes()
        .then(response => {
          this.leaveTypeOptions = Array.isArray(response.data) ? response.data : []
        })
        .catch(() => {
          this.typesError = 'Leave types could not be loaded.'
        })
        .finally(() => {
          this.typesLoading = false
        })
    },
    onLeaveTypeChange() {
      this.form.parental_date = null
      this.form.from_date = null
      this.form.to_date = null
      this.form.proof = null
      this.$v.form.parental_date.$reset()
      this.$v.form.from_date.$reset()
      this.$v.form.to_date.$reset()
      this.$v.form.proof.$reset()
    },
    onParentalDateChange() {
      this.form.from_date = null
      this.form.to_date = null
      this.$v.form.from_date.$reset()
      this.$v.form.to_date.$reset()
    },
    isParentalDateDisabled(date) {
      if (!this.form.type || this.form.type.code !== 'paternity_leave') return false
      return dateValue(date) > dateValue(addMonths(new Date(), 6))
    },
    isFromDateDisabled(date) {
      if (!this.form.type) return false
      const value = dateValue(date)
      const today = dateValue(new Date())
      if (['paid_leave', 'unpaid_leave'].includes(this.form.type.code)) {
        return value < today || value > dateValue(addYears(new Date(), 1))
      }
      if (this.form.type.code === 'sick_leave') {
        return value < dateValue(subDays(new Date(), 1)) || value > today
      }
      if (this.form.type.code === 'paternity_leave' && this.form.parental_date) {
        return value < dateValue(subDays(parseISO(this.form.parental_date), 3))
          || value > dateValue(addMonths(parseISO(this.form.parental_date), 6))
      }
      return false
    },
    isToDateDisabled(date) {
      if (!this.form.from_date || !this.form.type) return false
      const maximumDays = this.maximumLeaveDays
      const value = dateValue(date)
      return value < this.form.from_date
        || Boolean(maximumDays && value > dateValue(addDays(parseISO(this.form.from_date), maximumDays - 1)))
    },
    submit(approve = false) {
      this.$v.form.$touch()
      if (this.$v.form.$invalid || this.submitting) return

      this.submitting = true
      this.submitError = ''
      let proof = null
      if (this.proofShownOnCreate && this.form.proof && this.form.proof.id) proof = this.form.proof.id

      const createRequest = approve === true ? postEmployeesLeaveCreateAndApprove : postEmployeesLeaveRequest
      createRequest({
        employee: this.employee.id,
        type: this.form.type.code,
        from_date: this.form.from_date,
        to_date: this.form.to_date,
        parental_date: this.parentalDateRequired ? this.form.parental_date : null,
        reason: this.form.reason,
        proof
      })
        .then(response => {
          this.$modal.hide(this.modalName)
          this.$notify({ type: 'success', title: 'Leave request created' })
          this.$emit('created', response.data)
        })
        .catch(error => {
          this.submitError = this.errorMessage(error)
        })
        .finally(() => {
          this.submitting = false
        })
    },
    errorMessage(error) {
      const response = error && error.response
      if (response && response.status === 403) return 'You do not have permission to create leave requests.'
      const data = response && response.data
      return (data && (data.details || data.detail || data.non_field_errors))
        || 'The leave request could not be created. Check the form and try again.'
    }
  }
}
</script>

<style lang="scss">
.employee-leave-request-modal .v--modal {
  max-height: calc(100vh - 32px);
  border-radius: $ui-overlay-radius;
  box-shadow: 0 20px 55px rgba(23, 36, 58, .2);
  overflow: hidden;
}

.employee-leave-request-modal__shell {
  display: flex;
  max-height: calc(100vh - 32px);
  flex-direction: column;
  background-color: $ui-surface;
  color: $ui-text;
  font-family: $ui-font-body;
}

.employee-leave-request-modal__form {
  display: flex;
  min-height: 0;
  flex-direction: column;
}

.employee-leave-request-modal__body {
  min-height: 0;
  padding: $ui-space-20 $ui-space-24;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.employee-leave-request-modal__employee {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $ui-space-16;
  margin-bottom: $ui-space-20;
  padding: $ui-space-12 $ui-space-16;
  border: 1px solid $ui-line;
  border-radius: $ui-panel-radius;
  background-color: $ui-surface-soft;

  span {
    color: $ui-muted;
    font-size: $ui-font-size-metadata;
    font-weight: 600;
  }

  strong {
    min-width: 0;
    color: $ui-ink;
    font-family: $ui-font-heading;
    font-size: $ui-font-size-body;
    font-weight: 600;
    text-align: right;
    overflow-wrap: anywhere;
  }
}

.employee-leave-request-modal__dates {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: $ui-space-12;
}

.employee-leave-request-modal__alert {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $ui-space-12;
  margin-bottom: $ui-space-16;
  padding: $ui-space-12 $ui-space-16;
  border: 1px solid $ui-red;
  border-radius: $ui-control-radius;
  background-color: $ui-red-soft;
  color: $ui-red;
  font-size: $ui-font-size-body;
  line-height: 1.45;
}

.employee-leave-request-modal__footer {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: flex-end;
  gap: $ui-space-12;
  padding: $ui-space-16 $ui-space-24 $ui-space-20;
  border-top: 1px solid $ui-line;
  background-color: $ui-surface;
}

@media (max-width: 600px) {
  .employee-leave-request-modal .v--modal {
    max-height: 100vh;
    border-radius: 0;
  }

  .employee-leave-request-modal__shell {
    max-height: 100vh;
  }

  .employee-leave-request-modal__body,
  .employee-leave-request-modal__footer {
    padding-right: $ui-space-16;
    padding-left: $ui-space-16;
  }

  .employee-leave-request-modal__dates {
    grid-template-columns: 1fr;
    gap: 0;
  }
}
</style>
